import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Navigation store — cross-page context + contextual back link
// Persisted to sessionStorage so context survives page refreshes but not
// new browser sessions.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'discoverant_nav_context';

interface NavigationStoreState {
  // Project / file / molecule context
  projectId: number | null;
  projectName: string | null;
  fileId: number | null;
  fileName: string | null;
  moleculeId: number | null;
  moleculeName: string | null;

  // Contextual back link
  previousPath: string | null;
  previousLabel: string | null;
}

interface NavigationStoreActions {
  setProject: (id: number | null, name?: string | null) => void;
  setFile: (id: number | null, name?: string | null) => void;
  setMolecule: (id: number | null, name?: string | null) => void;
  setPreviousNavigation: (path: string, label: string) => void;
  clearPreviousNavigation: () => void;
  hydrateFromSearchParams: (params: URLSearchParams) => void;
  clearContext: () => void;
}

type NavigationStore = NavigationStoreState & NavigationStoreActions;

function loadFromSession(): Partial<NavigationStoreState> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

function saveToSession(state: NavigationStoreState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      projectId: state.projectId,
      projectName: state.projectName,
      fileId: state.fileId,
      fileName: state.fileName,
      moleculeId: state.moleculeId,
      moleculeName: state.moleculeName,
      previousPath: state.previousPath,
      previousLabel: state.previousLabel,
    }));
  } catch { /* ignore */ }
}

const persisted = loadFromSession();

const defaultState: NavigationStoreState = {
  projectId: null,
  projectName: null,
  fileId: null,
  fileName: null,
  moleculeId: null,
  moleculeName: null,
  previousPath: null,
  previousLabel: null,
};

export const useNavigationStore = create<NavigationStore>((set, get) => ({
  ...defaultState,
  ...persisted,

  setProject: (id, name) => {
    set({ projectId: id, projectName: name ?? null });
    saveToSession(get());
  },

  setFile: (id, name) => {
    set({ fileId: id, fileName: name ?? null });
    saveToSession(get());
  },

  setMolecule: (id, name) => {
    set({ moleculeId: id, moleculeName: name ?? null });
    saveToSession(get());
  },

  setPreviousNavigation: (path, label) => {
    set({ previousPath: path, previousLabel: label });
    saveToSession(get());
  },

  clearPreviousNavigation: () => {
    set({ previousPath: null, previousLabel: null });
    saveToSession(get());
  },

  hydrateFromSearchParams: (params: URLSearchParams) => {
    const updates: Partial<NavigationStoreState> = {};
    const current = get();

    const pid = params.get('project_id');
    if (pid) {
      updates.projectId = Number(pid);
      const pname = params.get('project_name');
      if (pname) updates.projectName = pname;
      else if (Number(pid) !== current.projectId) updates.projectName = null;
    }
    // Also support ?project= (used by FilesPage)
    const projectAlt = params.get('project');
    if (!pid && projectAlt) {
      updates.projectId = Number(projectAlt);
      if (Number(projectAlt) !== current.projectId) updates.projectName = null;
    }

    const fid = params.get('file_id');
    if (fid) {
      updates.fileId = Number(fid);
      const fname = params.get('file_name');
      if (fname) updates.fileName = fname;
      else if (Number(fid) !== current.fileId) updates.fileName = null;
    }

    const mid = params.get('molecule_id');
    if (mid) {
      updates.moleculeId = Number(mid);
      const mname = params.get('molecule_name');
      if (mname) updates.moleculeName = mname;
      else if (Number(mid) !== current.moleculeId) updates.moleculeName = null;
    }

    if (Object.keys(updates).length > 0) {
      set(updates);
      saveToSession({ ...current, ...updates } as NavigationStoreState);
    }
  },

  clearContext: () => {
    set({ ...defaultState });
    saveToSession(defaultState);
  },
}));
